import React, { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Divider,
  Chip,
  Alert,
  TextField,
  Tooltip,
  IconButton,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import TelegramIcon from "@mui/icons-material/Telegram";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const GLASS = {
  surface: "linear-gradient(180deg, rgba(12,16,22,.92) 0%, rgba(8,11,17,.92) 60%)",
  border: "1px solid rgba(255,255,255,.08)",
  shadow: "0 10px 32px rgba(2,8,23,.45)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

function canUseClipboard() {
  return typeof navigator !== "undefined" && !!navigator.clipboard?.writeText;
}

async function copyToClipboard(text) {
  if (!canUseClipboard()) throw new Error("Clipboard is not available in this browser context.");
  await navigator.clipboard.writeText(text);
}

export default function SupportPanel({
  supportEmail = "support@promospay.app",
  telegramUrl = "https://t.me/promospay_support",
  onOpenChat = null, // e.g. Crisp/Intercom open handler
  brandName = "PromosPay",
}) {
  const [message, setMessage] = useState("");
  const [toast, setToast] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const subject = useMemo(() => `${brandName} Support`, [brandName]);

  const body = useMemo(() => {
    const text = String(message || "").trim();
    if (!text) {
      return [
        "Hello Support Team,",
        "",
        "Please help me with the issue below:",
        "",
        "(Describe the problem here)",
        "",
        "---",
        "Helpful info: UID (from profile), screenshots, device/browser, and steps to reproduce.",
      ].join("\n");
    }
    return [
      "Hello Support Team,",
      "",
      text,
      "",
      "---",
      "Helpful info: UID (from profile), screenshots, device/browser, and steps to reproduce.",
    ].join("\n");
  }, [message]);

  const mailtoHref = useMemo(() => {
    return `mailto:${encodeURIComponent(supportEmail)}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  }, [supportEmail, subject, body]);

  const showToast = (type, text) => setToast({ type, text });

  const copy = async (key, value) => {
    setToast(null);
    try {
      await copyToClipboard(value);
      setCopiedKey(key);
      showToast("success", "Copied to clipboard.");
      window.setTimeout(() => setCopiedKey(null), 1600);
    } catch (e) {
      showToast("error", e?.message || "Failed to copy.");
    }
  };

  const openChat = () => {
    setToast(null);
    if (typeof onOpenChat === "function") {
      onOpenChat();
      return;
    }
    showToast("info", "Live chat is not configured yet. Please use Email or Telegram.");
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        background: GLASS.surface,
        border: GLASS.border,
        boxShadow: GLASS.shadow,
        color: GLASS.text,
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
          <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Support</Typography>
          <Chip
            size="small"
            label="24/7"
            sx={{
              height: 22,
              borderRadius: 1,
              bgcolor: "rgba(56,189,248,.14)",
              color: GLASS.text,
              border: "1px solid rgba(255,255,255,.10)",
              fontWeight: 900,
            }}
          />
        </Box>

        <Typography sx={{ color: GLASS.subtext, fontSize: 13, lineHeight: 1.7, mb: 1.5 }}>
          Choose a channel below. For faster resolution, include your UID (from profile), steps to reproduce, and a screenshot.
        </Typography>

        <Divider sx={{ mb: 1.5, borderColor: "rgba(255,255,255,.08)" }} />

        {/* Live Chat */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <ChatBubbleIcon fontSize="small" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950 }}>Live chat</Typography>
            <Typography sx={{ color: GLASS.subtext, fontSize: 12 }}>
              Best for quick questions and real-time troubleshooting.
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={openChat}
            startIcon={<ChatBubbleIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 950,
              borderRadius: 2,
              background:
                "linear-gradient(135deg, rgba(56,189,248,1) 0%, rgba(59,130,246,1) 55%, rgba(99,102,241,1) 120%)",
              boxShadow: "0 12px 30px rgba(56,189,248,.18)",
              whiteSpace: "nowrap",
            }}
          >
            Open chat
          </Button>
        </Box>

        <Divider sx={{ my: 1.5, borderColor: "rgba(255,255,255,.08)" }} />

        {/* Email */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
          <EmailIcon fontSize="small" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950 }}>Email</Typography>
            <Typography sx={{ color: GLASS.subtext, fontSize: 12 }}>
              Best for detailed reports and attachments.
            </Typography>
          </Box>

          <Tooltip title="Copy email address">
            <IconButton
              onClick={() => copy("email", supportEmail)}
              sx={{
                color: GLASS.text,
                border: "1px solid rgba(255,255,255,.10)",
                borderRadius: 2,
              }}
            >
              {copiedKey === "email" ? (
                <CheckCircleIcon fontSize="small" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Button
            component="a"
            href={mailtoHref}
            target="_blank"
            rel="noreferrer"
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 950,
              borderRadius: 2,
              borderColor: "rgba(255,255,255,.16)",
              color: GLASS.text,
              whiteSpace: "nowrap",
            }}
          >
            Compose
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}>
          <TextField
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Optional: write a short summary (included in the email body)"
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "rgba(15,23,42,.45)",
                color: "#e5e7eb",
                "& fieldset": { borderColor: "rgba(148,163,184,.25)" },
                "&:hover fieldset": { borderColor: "rgba(56,189,248,.35)" },
                "&.Mui-focused fieldset": { borderColor: "rgba(56,189,248,.55)" },
              },
            }}
          />
        </Box>

        <Divider sx={{ my: 1.5, borderColor: "rgba(255,255,255,.08)" }} />

        {/* Telegram */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TelegramIcon fontSize="small" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950 }}>Telegram</Typography>
            <Typography sx={{ color: GLASS.subtext, fontSize: 12 }}>
              Quick mobile-first support channel.
            </Typography>
          </Box>

          <Tooltip title="Copy Telegram link">
            <IconButton
              onClick={() => copy("telegram", telegramUrl)}
              sx={{
                color: GLASS.text,
                border: "1px solid rgba(255,255,255,.10)",
                borderRadius: 2,
              }}
            >
              {copiedKey === "telegram" ? (
                <CheckCircleIcon fontSize="small" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Button
            component="a"
            href={telegramUrl}
            target="_blank"
            rel="noreferrer"
            variant="contained"
            startIcon={<TelegramIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 950,
              borderRadius: 2,
              background:
                "linear-gradient(135deg, rgba(34,197,94,1) 0%, rgba(16,185,129,1) 60%, rgba(59,130,246,1) 140%)",
              boxShadow: "0 12px 30px rgba(34,197,94,.12)",
              whiteSpace: "nowrap",
            }}
          >
            Open
          </Button>
        </Box>

        {toast ? (
          <Alert severity={toast.type} sx={{ mt: 1.5, bgcolor: "rgba(0,0,0,.2)", color: GLASS.text }}>
            {toast.text}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
